import { FC, type ReactElement } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LayerGroup, Marker, Polyline, Popup } from "react-leaflet";
import { tripNodeDivIcon } from "../../../../../../components/charts/GraphMap/utils";
import { TransportArc, TransportMode, TransportNode } from "../../../../../../datasources/carbonsnap/models";
import { resolveDefaultNodeTypeId } from "../../../../../../datasources/carbonsnap/utils";
import { useChartColors } from "../../../../../../theme/theme";
// Reuse the shared map popup styles
import "../../../../../../components/charts/GraphMap/layers/MarkerLayer/style.css";

interface Props {
	nodes: TransportNode[];
	arcs: TransportArc[];
}

const formatAddress = (node: TransportNode): string => {
	const parts: string[] = [];

	if (node.address) parts.push(node.address);

	if (node.zipCode || node.city) {
		const cityPart = [node.zipCode, node.city].filter(Boolean).join(" ");
		if (cityPart) parts.push(cityPart);
	}

	if (node.province && parts.length > 0) {
		parts[parts.length - 1] += ` ${node.province}`;
	}

	if (node.country) parts.push(node.country);

	return parts.join(", ");
};

const CarbonSnapLayer: FC<Props> = ({ nodes, arcs }) => {
	const { t } = useTranslation("whatIf.carbonsnap");
	const { chartColors } = useChartColors();

	const validNodes = nodes.filter((node) => node.lat !== null && node.lng !== null);

	if (validNodes.length === 0) return null;

	const getTransportModeColor = (mode: TransportMode | null): string => {
		switch (mode) {
			case TransportMode.Road:
				return chartColors.roadType;
			case TransportMode.Train:
				return chartColors.trainType;
			case TransportMode.Sea:
				return chartColors.shipType;
			case TransportMode.Air:
				return chartColors.airType;
			case TransportMode.Water:
				// Water uses the same color as Sea (waterborne transport)
				return chartColors.shipType;
			default:
				return "#888";
		}
	};

	const getNodeColor = (nodeIndex: number, totalNodes: number): string => {
		if (nodeIndex === 0) return "#0F0"; // Verde per partenza
		if (nodeIndex === totalNodes - 1) return "#F00"; // Rosso per arrivo
		return "#CCC"; // Grigio per nodi intermedi
	};

	const markers = validNodes.map((node, index) => {
		const originalIndex = nodes.indexOf(node);
		const nodeTypeId = node.nodeTypeId ?? resolveDefaultNodeTypeId(node.codeType, originalIndex, nodes.length);
		const nodeColor = getNodeColor(originalIndex, nodes.length);
		const formattedAddress = formatAddress(node);
		const isGeolocated = node.isGeolocated;

		return (
			<Marker
				key={`node-${node.id}-${nodeTypeId}`}
				position={[node.lat!, node.lng!]}
				icon={tripNodeDivIcon(nodeTypeId, nodeColor, 32)}
			>
				<Popup className="custom-popup" closeButton={true} autoPan={false}>
					<Box sx={{ minWidth: 250, p: 1 }}>
						<Typography
							variant="subtitle1"
							fontWeight="bold"
							sx={{ m: 0, p: 0, mb: 0.25, display: "block" }}
						>
							{node.name || t("route/node/selected node")}
						</Typography>

						<Divider sx={{ my: 0.25 }} />

						{!isGeolocated && (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									rowGap: 0,
									"& .MuiTypography-root": { lineHeight: 1.1, m: 0 },
								}}
							>
								{node.code && (
									<Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0 }}>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ fontSize: "0.875rem", p: 0, display: "block" }}
										>
											Codice/GLN
										</Typography>
										<Typography
											variant="body2"
											fontWeight="medium"
											textAlign="right"
											sx={{ fontSize: "0.875rem", p: 0, display: "block" }}
										>
											{node.code}
										</Typography>
									</Box>
								)}

								{node.emissions !== undefined && (
									<Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0 }}>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ fontSize: "0.875rem", p: 0, display: "block" }}
										>
											Emissioni
										</Typography>
										<Typography
											variant="body2"
											fontWeight="medium"
											textAlign="right"
											sx={{ fontSize: "0.875rem", p: 0, display: "block" }}
										>
											{node.emissions} tCO2e
										</Typography>
									</Box>
								)}

								{node.nodeTypeName && (
									<Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0 }}>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ fontSize: "0.875rem", p: 0, display: "block" }}
										>
											Tipologia
										</Typography>
										<Typography
											variant="body2"
											fontWeight="medium"
											textAlign="right"
											sx={{ fontSize: "0.875rem", p: 0, display: "block" }}
										>
											{node.nodeTypeName}
										</Typography>
									</Box>
								)}
							</Box>
						)}

						{formattedAddress && (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ fontSize: "0.8125rem", m: 0, mt: 0.5, p: 0, display: "block" }}
							>
								{formattedAddress}
							</Typography>
						)}
						{isGeolocated && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ fontSize: "0.75rem", m: 0, mt: 0.25, p: 0, display: "block" }}
							>
								{node.lat?.toFixed(6)}, {node.lng?.toFixed(6)}
							</Typography>
						)}
					</Box>
				</Popup>
			</Marker>
		);
	});

	const polylines = arcs
		.map((arc) => {
			const fromNode = nodes.find((node) => node.id === arc.fromNodeId);
			const toNode = nodes.find((node) => node.id === arc.toNodeId);
			if (
				!fromNode ||
				!toNode ||
				fromNode.lat === null ||
				fromNode.lng === null ||
				toNode.lat === null ||
				toNode.lng === null
			) {
				return null;
			}
			return (
				<Polyline
					key={`arc-${arc.id}`}
					positions={[
						[fromNode.lat, fromNode.lng],
						[toNode.lat, toNode.lng],
					]}
					pathOptions={{
						color: getTransportModeColor(arc.transportMode),
						weight: 4,
						opacity: 0.8,
					}}
				/>
			);
		})
		.filter((line): line is ReactElement => Boolean(line));

	return (
		<LayerGroup>
			{polylines}
			{markers}
		</LayerGroup>
	);
};

export default CarbonSnapLayer;
